import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { combineLatest, Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import {
  ActivateDeactivateCommands,
  DeviceConnectionStatus,
  DeviceHeatingCircuitIdCommands,
  Feature,
  FeatureCommands,
  FeatureNames,
  FeaturePropertyKey,
  FhtDemandRelayModeCommand,
  FhtDemandRelayModeValues,
  IsActiveProperty,
  IsEnabledProperty,
  IsNumberValueProperty,
  IsStringValueProperty,
} from '@vi-iot/client';

import { GTMService } from '@services';
import {
  CommissioningStore,
  GatewayDevice,
  GatewayStore,
  InstallationDetailsStore,
  InstallationFeaturesStore,
  RemoteDeviceType,
  RoomActor,
  getFhtMainFromChannel,
  isReady,
  isRemoteDeviceOfType,
} from '@store';

import { MatDialog } from '@angular/material/dialog';
import { InstallationService } from '../../../../../services/installation.service';
import { CommissioningDeleteDeviceConfirmModalComponent } from '../../../commissioning';
import { CommissioningService } from '../../../commissioning/commissioning.service';
import { CloseEventDialog } from '../../../commissioning/commissioning-delete-device-confirm-modal/commissioning-delete-device-confirm-modal.component';

@Component({
  selector: 'app-src-device-panel',
  templateUrl: './src-device-panel.component.html',
  styleUrls: ['./src-device-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SrcDevicePanelComponent implements OnInit {
  readonly srcType = RemoteDeviceType;
  readonly FeatureCommands = FeatureCommands;
  readonly featurePropertyKey = FeaturePropertyKey;

  circuits$: Observable<Feature<IsEnabledProperty, never>>;
  mountingMode$: Observable<Feature<IsActiveProperty, never>>;
  burnInMode$: Observable<Feature<IsActiveProperty, ActivateDeactivateCommands>>;
  fhtDemandRelayMode$: Observable<Feature<IsStringValueProperty, FhtDemandRelayModeCommand>>;
  connection$: Observable<Feature<DeviceConnectionStatus, never>>;
  fhtMainUnitConnection$: Observable<Feature<DeviceConnectionStatus, never>>;
  installationTimezone$: Observable<string>;

  @Input() roomId: string;

  @Input() device: GatewayDevice | RoomActor;

  @Input() heatingCircuitIdFeature$: Observable<
    Feature<IsNumberValueProperty, DeviceHeatingCircuitIdCommands>
  >;

  @Input() isDeleteable = false;

  @Output() unassignDevice: EventEmitter<string> = new EventEmitter<string>();

  get deviceId(): string {
    return (
      (this.device as GatewayDevice)?.id ||
      null ||
      (this.device as { deviceId: string; heatingCircuit: number })?.deviceId ||
      null
    );
  }

  readonly isRemoteDeviceOfType = isRemoteDeviceOfType;
  readonly fhtDemandRelayModeValues = FhtDemandRelayModeValues;

  constructor(
    public commissioningStore: CommissioningStore,
    public installationService: InstallationService,
    public installationFeaturesStore: InstallationFeaturesStore,
    public gatewayStore: GatewayStore,
    public commissioningService: CommissioningService,
    private gtmService: GTMService,
    private installationDetailsStore: InstallationDetailsStore,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.circuits$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.circuits
    );
    this.mountingMode$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.etrvMountingMode,
      this.deviceId
    );
    this.fhtDemandRelayMode$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.fhtDemandRelayMode,
      this.deviceId
    );
    this.burnInMode$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.fhtChannelBurnInMode,
      this.deviceId
    );
    this.connection$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.deviceConnection,
      this.deviceId
    );
    this.fhtMainUnitConnection$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.deviceConnection,
      getFhtMainFromChannel(this.deviceId)
    );
    this.installationTimezone$ = this.installationDetailsStore.installationTimezone$;
  }

  isDeviceFHTChannel(deviceId: string): Observable<boolean> {
    return this.gatewayStore.isDeviceOfType(deviceId, RemoteDeviceType.FHT_CHANNEL);
  }

  getHeatingCircuitId(): Observable<string> {
    return this.heatingCircuitIdFeature$.pipe(
      filter((heatingCircuitFeature) => isReady(heatingCircuitFeature)),
      map((readyHeatingCircuitIdFeature) => {
        const heatingCircuitId = readyHeatingCircuitIdFeature?.properties?.value?.value;

        return Number.isInteger(heatingCircuitId) ? `${heatingCircuitId}` : null;
      })
    );
  }

  isDeviceETRVOrFHT(deviceId: string): Observable<boolean> {
    const isETRV = this.gatewayStore.isDeviceOfType(deviceId, RemoteDeviceType.TRV);
    const isFHTMain = this.gatewayStore.isDeviceOfType(deviceId, RemoteDeviceType.FHT);
    const isFHTChannel = this.isDeviceFHTChannel(deviceId);

    return combineLatest([isETRV, isFHTMain, isFHTChannel]).pipe(
      map((result) => result[0] || result[1] || result[2])
    );
  }

  isFHTChannelWithoutHeatingCircuit(deviceId: string): Observable<boolean> {
    return combineLatest([this.isDeviceFHTChannel(deviceId), this.getHeatingCircuitId()]).pipe(
      map((result) => {
        const isDeviceFHTChannel = result[0];
        const isHeatinCircuitAssigned = result[1];

        return isDeviceFHTChannel && !isHeatinCircuitAssigned;
      })
    );
  }

  onAssignHeatingCircuit(circuitId: string, deviceId: string): void {
    this.installationFeaturesStore.updateFeature(
      FeatureNames.deviceHeatingCircuitId,
      Number(circuitId),
      null,
      this.featurePropertyKey.value,
      FeatureCommands.assignHeatingCircuit,
      null,
      deviceId
    );
  }

  unassignDeviceFromRoomAttempt(deviceId: string): void {
    const dialogRef = this.dialog.open(CommissioningDeleteDeviceConfirmModalComponent, {
      width: '715px',
      panelClass: 'commissioning-delete-device-confirm-dialog',
      data: {
        deviceId,
        titleKey: '+SRC_COMMISSIONINGV2.UNASSIGN_DEVICE_TITLE',
        normalMessageKey: '+SRC_COMMISSIONINGV2.UNASSIGN_DEVICE_CONFIRM_TEXT',
        lastDeviceMessageKey: '+SRC_COMMISSIONINGV2.UNASSIGN_DEVICE_LAST_DEVICE_CONFIRM_TEXT',
      },
    });

    dialogRef.afterClosed().subscribe((event: CloseEventDialog) => {
      if (event.confirmed) {
        this.unassignDevice.emit(deviceId);
        this.gtmService.track('onUnassignDevice');
        if (event.shouldDeactivateSRC) {
          this.commissioningService.deactivateSRC();
        }
      } else {
        this.gtmService.track('onCancel', 'Delete device from room confirmation', 'devicePanel');
      }
    });
  }

  deleteDeviceFromRoomAttempt(): void {
    const dialogRef = this.dialog.open(CommissioningDeleteDeviceConfirmModalComponent, {
      width: '715px',
      panelClass: 'commissioning-delete-device-confirm-dialog',
      data: {
        deviceId: this.deviceId,
        lastDeviceMessageKey: '+SRC_COMMISSIONINGV2.UNASSIGN_DEVICE_LAST_DEVICE_CONFIRM_TEXT',
      },
    });

    dialogRef.afterClosed().subscribe((event: CloseEventDialog) => {
      if (event.confirmed) {
        this.commissioningService.deleteDevice(this.device as GatewayDevice);
        this.gtmService.track('onConfirm', 'Delete device', 'devicePanel');
        if (event.shouldDeactivateSRC) {
          this.commissioningService.deactivateSRC();
        }
      } else {
        this.gtmService.track('onCancel', 'Delete device', 'devicePanel');
      }
    });
  }

  onBurnInModeChange(event: MouseEvent, value: boolean): void {
    event.preventDefault();
    event.stopImmediatePropagation();
    this.installationFeaturesStore.updateFeature(
      FeatureNames.fhtChannelBurnInMode,
      value,
      this.featurePropertyKey.active,
      null,
      value ? FeatureCommands.activate : FeatureCommands.deactivate,
      null,
      this.deviceId
    );
  }

  onFHTDemandRelayModeChange(event: MatSlideToggleChange): void {
    this.installationFeaturesStore.updateFeature(
      FeatureNames.fhtDemandRelayMode,
      event.checked ? FhtDemandRelayModeValues.automatic : FhtDemandRelayModeValues.off,
      this.featurePropertyKey.value,
      null,
      FeatureCommands.setValue,
      null,
      this.deviceId
    );
  }
}
